import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ParamContext } from '../shared/service/param.context';
import { FileService } from '../shared/file/service/file.service';
import { FileMetadata } from '../shared/file/model/file-metadata';
import { readAsDataURL } from '../shared/util/file-utils';
import { HttpResponse } from '@angular/common/http';
import { AlertService } from '../shared/alert/alert.service';
import { finalize, map } from 'rxjs/operators';
import { ModalConfirmComponent } from '../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';
import {
    COOPERATOR_CVTERM_ID,
    EXPT_DESIGN_CVTERM_ID,
    FILE_UPLOAD_SUPPORTED_TYPES,
    LOCATION_ID_CVTERM_ID,
    PI_NAME_CVTERM_ID,
    TINY_BLANK_IMAGE,
    TRIAL_INSTANCE_CVTERM_ID
} from '../app.constants';
import { VariableDetails } from '../shared/ontology/model/variable-details';
import { FilterType } from '../shared/column-filter/column-filter.component';
import { Pageable } from '../shared/model/pageable';
import { VariableTypeEnum } from '../shared/ontology/variable-type.enum';
import {
    MANAGE_FILES_ENVIRONMENT_PERMISSION,
    MG_MANAGE_FILES_PERMISSION,
    MI_MANAGE_FILES_PERMISSION,
    MS_MANAGE_FILES_OBSERVATIONS_PERMISSION,
    MS_MANAGE_FILES_STUDY_SETTINGS_PERMISSION
} from '../shared/auth/permissions';
import { Principal } from '../shared';
import { JhiLanguageService } from '../shared/core-utils';
import { FileDownloadHelper } from '../entities/sample/file-download.helper';

@Component({
    selector: 'jhi-file-manager',
    templateUrl: './file-manager.component.html',
    styleUrls: ['./file-manager.scss']
})
export class FileManagerComponent implements OnInit {

    /**
     * Regex pattern used to sanitize file names while preserving Unicode letters and digits.
     * <p>
     * Matches (and therefore removes/replaces) any character that is NOT:
     *   - \p{L} : a Unicode letter, in any script (a-z, A-Z, 中文, é, Ñ, Cyrillic, Arabic, etc.)
     *   - \p{N} : a Unicode digit, in any script (0-9, full-width digits, Arabic-indic digits, etc.)
     *   - ' '   : literal space
     *   - '.'   : literal dot (needed for file extensions)
     *   - '_'   : literal underscore
     *   - '-'   : literal hyphen
     * <p>
     * This effectively whitelists safe filename characters instead of blacklisting unsafe ones,
     * so it naturally strips filesystem-reserved characters on both Windows and Unix-like systems,
     * including: / \ : * ? " < > |  as well as control characters and other symbols.
     * <p>
     * NOTE: '.' and '-' are preserved, so ".." sequences are NOT removed by this pattern alone.
     * This is sufficient for character-level filename safety, but does NOT by itself prevent
     * path traversal. If this sanitized value is ever combined with a path separator elsewhere
     * (e.g. Paths.get(baseDir, sanitizedName)), it must still be passed through
     * Paths.get(...).normalize() and checked against the intended base directory as a separate,
     * independent guard.
     */
    private static readonly SAFE_FILENAME_PATTERN = /[^\p{L}\p{N} ._-]/u;

    @ViewChild('fileUpload', { static: true }) fileUpload: ElementRef;
    @ViewChild('img') img: ElementRef;

    VARIABLE_TYPE_IDS;
    manageFilesPermissions = [];
    variable: VariableDetails;
    file: File;
    fileMetadataSelected: FileMetadata;
    fileMetadataList: FileMetadata[];
    imgToUploadUrlPreview;
    observationUnitUUID: string;
    variableName: string;
    datasetId: number;
    instanceId: number;
    projectId: number;
    lotId: number;
    germplasmUUID: string;
    isLoading = false;
    isLoadingImage = false;
    embedded = false;
    acceptedFileTypes = (FILE_UPLOAD_SUPPORTED_TYPES || '').split(',').map((t) => '.' + t).join(',');
    excludedVariableIds = [TRIAL_INSTANCE_CVTERM_ID, EXPT_DESIGN_CVTERM_ID, LOCATION_ID_CVTERM_ID, COOPERATOR_CVTERM_ID, PI_NAME_CVTERM_ID];
    filters = {
        variable: {
            key: 'variable',
            type: FilterType.TEXT,
            value: ''
        }
    };

    page = 1;
    pageSize = 5;
    totalCount: any;

    constructor(
        public jhiLanguageService: JhiLanguageService,
        public route: ActivatedRoute,
        private activeModal: NgbActiveModal,
        public context: ParamContext,
        private fileService: FileService,
        private alertService: AlertService,
        private modalService: NgbModal,
        private translateService: TranslateService,
        private principal: Principal,
        private fileDownloadHelper: FileDownloadHelper
    ) {
        this.context.readParams();
    }

    load() {
        this.isLoading = true;
        this.fileService.listFileMetadata(
            this.observationUnitUUID,
            this.germplasmUUID,
            this.filters.variable.value,
            this.instanceId,
            this.lotId,
            this.projectId,
            <Pageable>({
                page: this.page - 1,
                size: this.pageSize,
                sort: null
            }),
        ).pipe(
            finalize(() => this.isLoading = false),
            map((resp) => {
                this.totalCount = resp.headers.get('X-Total-Count');
                return resp;
            })
        ).subscribe((resp) => {
            this.fileMetadataSelected = null;
            this.imgToUploadUrlPreview = null;
            this.fileMetadataList = resp.body;
        });
    }

    async ngOnInit() {
        const currentLang = this.jhiLanguageService.getCurrentLanguage();
        this.jhiLanguageService.changeLanguage(currentLang);

        const queryParamMap = this.route.snapshot.queryParamMap;
        if (queryParamMap.get('embedded')) {
            this.embedded = Boolean(queryParamMap.get('embedded'));
        }
        if (queryParamMap.get('variableName')) {
            this.filters.variable.value = queryParamMap.get('variableName');
        }
        if (queryParamMap.get('observationUnitUUID')) {
            this.observationUnitUUID = queryParamMap.get('observationUnitUUID');
        }
        if (queryParamMap.get('germplasmUUID')) {
            this.germplasmUUID = queryParamMap.get('germplasmUUID');
        }
        if (queryParamMap.get('instanceId')) {
            this.instanceId = Number(queryParamMap.get('instanceId'));
        }
        if (queryParamMap.get('lotId')) {
            this.lotId = Number(queryParamMap.get('lotId'));
        }
        if (queryParamMap.get('projectId')) {
            this.projectId = Number(queryParamMap.get('projectId'));
        }

        if (this.observationUnitUUID) {
            this.VARIABLE_TYPE_IDS = [VariableTypeEnum.TRAIT, VariableTypeEnum.SELECTION_METHOD];
            if (queryParamMap.get('datasetId')) {
                this.datasetId = Number(queryParamMap.get('datasetId'));
            }
            this.manageFilesPermissions = MS_MANAGE_FILES_OBSERVATIONS_PERMISSION;
        } else if (this.germplasmUUID) {
            this.VARIABLE_TYPE_IDS = [VariableTypeEnum.GERMPLASM_ATTRIBUTE, VariableTypeEnum.GERMPLASM_PASSPORT];
            this.manageFilesPermissions = MG_MANAGE_FILES_PERMISSION;
        } else if (this.instanceId) {
            if (queryParamMap.get('datasetId')) {
                this.datasetId = Number(queryParamMap.get('datasetId'));
            }
            this.VARIABLE_TYPE_IDS = [VariableTypeEnum.ENVIRONMENT_CONDITION, VariableTypeEnum.ENVIRONMENT_DETAIL];
            this.manageFilesPermissions = MANAGE_FILES_ENVIRONMENT_PERMISSION;
        } else if (this.lotId) {
            this.VARIABLE_TYPE_IDS = [VariableTypeEnum.INVENTORY_ATTRIBUTE];
            this.manageFilesPermissions = MI_MANAGE_FILES_PERMISSION;
        } else if (this.projectId) {
            this.VARIABLE_TYPE_IDS = [VariableTypeEnum.STUDY_DETAIL];
            this.manageFilesPermissions = MS_MANAGE_FILES_STUDY_SETTINGS_PERMISSION;
        }

        if (this.variableName) {
            this.filters.variable.value = this.variableName;
        }

        this.load();

        // get user account to use hasAnyAuthority directive
        await this.principal.identity();
    }

    applyFilters() {
        this.load();
    }

    resetFilters() {
        this.load();
    }

    isImage(fileName) {
        return fileName.match(/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i);
    }

    close() {
        this.activeModal.dismiss();
        if ((<any>window.parent).closeModal) {
            (<any>window.parent).closeModal();
        }
    }

    getFileKeyEncoded(path) {
        return encodeURIComponent(path);
    }

    async delete($event, fileMetadata: FileMetadata) {
        $event.stopPropagation();
        const confirmModal = this.modalService.open(ModalConfirmComponent);
        confirmModal.componentInstance.message = this.translateService.instant('fileManager.delete.confirm', { param: fileMetadata.name });
        try {
            await confirmModal.result;
        } catch (e) {
            return;
        }
        this.isLoading = true;
        this.fileService.delete(fileMetadata.fileUUID)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe(() => {
                this.alertService.success('fileManager.delete.success');
                this.load();
                if (window.parent) {
                    window.parent.postMessage('observations-changed', '*');
                }
            });
        return false;
    }

    download($event, fileMetadata: FileMetadata) {
        $event.stopPropagation();
        this.isLoading = true;
        this.fileService.downloadFile(fileMetadata.path).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe(
            (file: HttpResponse<Blob>) => {
                const fileName = this.fileDownloadHelper.getFileNameFromResponseContentDisposition(file);
                this.fileDownloadHelper.save(file.body, fileName);
            }
        );
    }

    async onFileChange(evt: any) {
        this.fileMetadataSelected = null;
        this.imgToUploadUrlPreview = null;
        this.file = evt.target.files[0];
        this.validateFileName();
        if (this.file && this.isImage(this.file.name)) {
            this.imgToUploadUrlPreview = await readAsDataURL(this.file);
        }
    }

    validateFileName() {
        if (FileManagerComponent.SAFE_FILENAME_PATTERN.test(this.file.name)) {
            this.alertService.error('fileManager.invalid.file.name');
            this.file = null;
        }
    }

    upload() {
        this.isLoading = true;
        // upload file / save observation
        this.fileService.upload(
            this.file,
            this.observationUnitUUID,
            this.germplasmUUID,
            this.variable && this.variable.id || null,
            this.instanceId,
            this.projectId,
            this.lotId
        ).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe(
            (fileMetadata) => {
                this.load();
                this.alertService.success('fileManager.upload.success');
                if (window.parent) {
                    window.parent.postMessage('observations-changed', '*');
                }
            });
    }

    select(fileMetadata: FileMetadata) {
        this.imgToUploadUrlPreview = null;
        this.isLoadingImage = true;
        this.fileMetadataSelected = fileMetadata;
    }

    onImageLoad() {
        if (this.img.nativeElement.src !== TINY_BLANK_IMAGE) {
            this.isLoadingImage = false;
        }
    }

}
